import { isNil, map, orderBy, uniqBy } from 'lodash';

import {
  AssignmentStageEnum,
  AssignmentStageModel,
  AssignmentTypeEnum,
  RenewKindEnum,
  RenewStatusEnum,
  StageStatusEnum,
} from 'api/backofficeApiClient';
import {
  ADMIN_HARD_SOFT_STOP_MESSAGES,
  ADMIN_SOFT_STOP_MESSAGES,
  arePreviousStagesCompleted,
  getProgressStepStatus,
  getStageMessage,
  isSoftHardStopStage,
  MEMBER_HARD_SOFT_STOP_MESSAGES,
  MEMBER_SOFT_STOP_MESSAGES,
  ProfileProgressStep,
  ProgressStepConfigParams,
  ProgressStepOptions,
  shouldDisplayRequestButton,
  shouldDisplayResponseButton,
  SOFT_HALF_STOP_MESSAGES,
} from './generalProgressUtils';
import { UserRole } from 'enums';

export const HAS_DUPLICATED_WORKER_ERROR =
  'Please approve or merge worker before proceeding with assignment approval.';

interface AssignmentProgressStepConfigParams extends ProgressStepConfigParams {
  step: AssignmentStageModel;
  isMemberQuestionnaireCompleteExists: boolean;
  isContractorQuestionnaireCompleteExists: boolean;
}
type AssignmentProgressStep = ProfileProgressStep<AssignmentStageEnum>;
type AssignmentProgressStepConfig = Partial<
  Record<
    AssignmentStageEnum,
    (step: AssignmentProgressStepConfigParams) => ProgressStepOptions
  >
>;

const W2_ASSIGNMENT_PROFILE_STAGES = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
) =>
  [
    AssignmentStageEnum.EECandidateSentOnboarding,
    AssignmentStageEnum.EERatesAndCustomerReviewed,
    AssignmentStageEnum.EECandidateCompletedOnboarding,
    AssignmentStageEnum.EEOnboardingPackageVerified,
    AssignmentStageEnum.EEPreScreeningValidating,
    AssignmentStageEnum.EEPreScreeningCompleted,
    AssignmentStageEnum.EETimesheetAccountCreated,
    AssignmentStageEnum.EEAssignmentConfirmed,
    // Add step for renewal request
    includeRenewStage ? AssignmentStageEnum.Renewed : undefined,
    includeRehireStage ? AssignmentStageEnum.Rehired : undefined,
  ].filter(Boolean) as Array<AssignmentStageEnum>;

const IC_ASSIGNMENT_PROFILE_STAGES = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
) =>
  [
    AssignmentStageEnum.ICMemberQuestionnaireComplete,
    AssignmentStageEnum.ICRatesAndCustomerReviewed,
    AssignmentStageEnum.ICContractorQuestionnaireComplete,
    AssignmentStageEnum.ICContractorChecklistVerified,
    AssignmentStageEnum.ICContractorSentPackage,
    AssignmentStageEnum.ICAcquiringApplicableInsurances,
    AssignmentStageEnum.ICPreScreeningValidating,
    AssignmentStageEnum.ICPreScreeningCompleted,
    AssignmentStageEnum.ICVendorProfileFinalized,
    AssignmentStageEnum.ICFinalAgreementUploaded,
    // Add step for renewal request
    includeRenewStage ? AssignmentStageEnum.Renewed : undefined,
    includeRehireStage ? AssignmentStageEnum.Rehired : undefined,
  ].filter(Boolean) as Array<AssignmentStageEnum>;

const SC_ASSIGNMENT_PROFILE_STAGES = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
) =>
  [
    AssignmentStageEnum.SubcontractorContractorSentPackage,
    AssignmentStageEnum.SubcontractorRatesAndCustomerReviewed,
    AssignmentStageEnum.SubcontractorWorkAuthorizationVerified,
    AssignmentStageEnum.SubcontractorPreScreeningValidating,
    AssignmentStageEnum.SubcontractorPreScreeningCompleted,
    AssignmentStageEnum.SubcontractorVendorProfileFinalized,
    AssignmentStageEnum.SubcontractorFinalAgreementUploaded,
    // Add step for renewal request
    includeRenewStage ? AssignmentStageEnum.Renewed : undefined,
    includeRehireStage ? AssignmentStageEnum.Rehired : undefined,
  ].filter(Boolean) as Array<AssignmentStageEnum>;

const ADMIN_W2_ASSIGNMENT_PROGRESS_STEPS = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.EECandidateSentOnboarding]: () => ({
      title: 'Candidate sent onboarding',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EERatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EECandidateCompletedOnboarding]: () => ({
      title: 'Candidate completed onboarding',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEOnboardingPackageVerified]: () => ({
      title: 'Onboarding package verified',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EETimesheetAccountCreated]: () => ({
      title: 'Timesheet account created',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEAssignmentConfirmed]: ({
      arePrevStagesCompleted,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Assignment confirmed',
      isActionEnabled: includeRenewStage ? true : arePrevStagesCompleted,
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStage =
      createAssignmentRenewOrRehireForAdminStageConfig(includeRehireStage);
    return { ...stages, ...lastStage };
  }

  return stages;
};

const ADMIN_IC_ASSIGNMENT_PROGRESS_STEPS = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.ICMemberQuestionnaireComplete]: ({
      isMemberQuestionnaireCompleteExists,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Member questionnaire complete',
      responsibleRole: isMemberQuestionnaireCompleteExists
        ? UserRole.Users
        : UserRole.Admin,
      messages: {
        softStopMessage: isMemberQuestionnaireCompleteExists
          ? ADMIN_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: isMemberQuestionnaireCompleteExists
          ? ADMIN_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICRatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorQuestionnaireComplete]: ({
      isContractorQuestionnaireCompleteExists,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Classification questionnaire complete',
      responsibleRole: isContractorQuestionnaireCompleteExists
        ? UserRole.Users
        : UserRole.Admin,
      messages: {
        softStopMessage: isContractorQuestionnaireCompleteExists
          ? ADMIN_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: isContractorQuestionnaireCompleteExists
          ? ADMIN_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorChecklistVerified]: () => ({
      title: 'Contractor checklist verified',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorSentPackage]: () => ({
      title: 'Contractor sent package',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICAcquiringApplicableInsurances]: () => ({
      title: 'Acquiring applicable insurances',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICVendorProfileFinalized]: () => ({
      title: 'Vendor profile finalized',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICFinalAgreementUploaded]: ({
      arePrevStagesCompleted,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Final agreement uploaded',
      isActionEnabled: includeRenewStage ? true : arePrevStagesCompleted,
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStage =
      createAssignmentRenewOrRehireForAdminStageConfig(includeRehireStage);
    return { ...stages, ...lastStage };
  }

  return stages;
};

const ADMIN_SC_ASSIGNMENT_PROGRESS_STEPS = (
  includeRenewStage: boolean,
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.SubcontractorContractorSentPackage]: () => ({
      title: 'Contractor sent package',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorRatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorWorkAuthorizationVerified]: () => ({
      title: 'Work authorization verified',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorVendorProfileFinalized]: () => ({
      title: 'Vendor profile finalized',
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorFinalAgreementUploaded]: ({
      arePrevStagesCompleted,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Final agreement uploaded',
      isActionEnabled: includeRenewStage ? true : arePrevStagesCompleted,
      messages: {
        softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
        hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStage =
      createAssignmentRenewOrRehireForAdminStageConfig(includeRehireStage);

    return { ...stages, ...lastStage };
  }

  return stages;
};

const createAssignmentRenewOrRehireForAdminStageConfig = (
  includeRehireStage: boolean,
) =>
  !includeRehireStage
    ? {
        [AssignmentStageEnum.Renewed]: ({
          arePrevStagesCompleted,
        }: AssignmentProgressStepConfigParams) => ({
          title: 'Assignment renewed',
          isActionEnabled: arePrevStagesCompleted,
          messages: {
            softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
            halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
            hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
          },
        }),
      }
    : {
        [AssignmentStageEnum.Rehired]: ({
          arePrevStagesCompleted,
        }: AssignmentProgressStepConfigParams) => ({
          title: 'Assignment rehired',
          isActionEnabled: arePrevStagesCompleted,
          messages: {
            softStopMessage: ADMIN_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
            halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.ADMIN,
            hardStopMessage: ADMIN_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
          },
        }),
      };

const createAssignmentRenewOrRehireForMemberStageConfig = (
  includeRehireStage: boolean,
) =>
  !includeRehireStage
    ? {
        [AssignmentStageEnum.Renewed]: () => ({
          title: 'Assignment renewed',
          messages: {
            softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
            halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
            hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
          },
        }),
      }
    : {
        [AssignmentStageEnum.Rehired]: () => ({
          title: 'Assignment rehired',
          messages: {
            softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
            halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
            hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
          },
        }),
      };

const MEMBER_W2_ASSIGNMENT_PROGRESS_STEPS = (
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.EECandidateSentOnboarding]: () => ({
      title: 'Candidate sent onboarding',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EERatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EECandidateCompletedOnboarding]: () => ({
      title: 'Candidate completed onboarding',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEOnboardingPackageVerified]: () => ({
      title: 'Onboarding package verified',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EETimesheetAccountCreated]: () => ({
      title: 'Timesheet account created',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.EEAssignmentConfirmed]: () => ({
      title: 'Assignment confirmed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStages =
      createAssignmentRenewOrRehireForMemberStageConfig(includeRehireStage);

    return { ...stages, ...lastStages };
  }

  return stages;
};

const MEMBER_IC_ASSIGNMENT_PROGRESS_STEPS = (
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.ICMemberQuestionnaireComplete]: ({
      step,
      isMemberQuestionnaireCompleteExists,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Member questionnaire complete',
      responsibleRole: isMemberQuestionnaireCompleteExists
        ? UserRole.Users
        : UserRole.Admin,
      isActionEnabled:
        isMemberQuestionnaireCompleteExists &&
        step.stageStatus !== StageStatusEnum.HardStop,
      actionBtnText: 'Fill in',
      messages: {
        pendingMessage:
          'Please complete the questionnaire in order to finish this stage',
        softStopMessage: isMemberQuestionnaireCompleteExists
          ? MEMBER_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: isMemberQuestionnaireCompleteExists
          ? MEMBER_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICRatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorQuestionnaireComplete]: ({
      step,
      isContractorQuestionnaireCompleteExists,
    }: AssignmentProgressStepConfigParams) => ({
      title: 'Classification questionnaire complete',
      responsibleRole: isContractorQuestionnaireCompleteExists
        ? UserRole.Users
        : UserRole.Admin,
      isActionEnabled:
        isContractorQuestionnaireCompleteExists &&
        step.stageStatus !== StageStatusEnum.HardStop,
      actionBtnText: 'Fill in',
      messages: {
        pendingMessage:
          'Please complete the questionnaire in order to finish this stage',
        softStopMessage: isContractorQuestionnaireCompleteExists
          ? MEMBER_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: isContractorQuestionnaireCompleteExists
          ? MEMBER_HARD_SOFT_STOP_MESSAGES.MEMBER_RESPONSIBLE
          : MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorChecklistVerified]: () => ({
      title: 'Contractor checklist verified',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICContractorSentPackage]: () => ({
      title: 'Contractor sent package',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICAcquiringApplicableInsurances]: () => ({
      title: 'Acquiring applicable insurances',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICVendorProfileFinalized]: () => ({
      title: 'Vendor profile finalized',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.ICFinalAgreementUploaded]: () => ({
      title: 'Final agreement uploaded',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStages =
      createAssignmentRenewOrRehireForMemberStageConfig(includeRehireStage);

    return { ...stages, ...lastStages };
  }

  return stages;
};

const MEMBER_SC_ASSIGNMENT_PROGRESS_STEPS = (
  includeRehireStage: boolean,
  hasRehireOrRenewStage: boolean,
) => {
  const stages = {
    [AssignmentStageEnum.SubcontractorContractorSentPackage]: () => ({
      title: 'Contractor sent package',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorRatesAndCustomerReviewed]: () => ({
      title: 'Rates and customer reviewed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorWorkAuthorizationVerified]: () => ({
      title: 'Work authorization verified',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorPreScreeningValidating]: () => ({
      title: 'Pre-screening validating',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorPreScreeningCompleted]: () => ({
      title: 'Pre-screening completed',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorVendorProfileFinalized]: () => ({
      title: 'Vendor profile finalized',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
    [AssignmentStageEnum.SubcontractorFinalAgreementUploaded]: () => ({
      title: 'Final agreement uploaded',
      messages: {
        softStopMessage: MEMBER_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
        halfSoftStopMessage: SOFT_HALF_STOP_MESSAGES.MEMBER,
        hardStopMessage: MEMBER_HARD_SOFT_STOP_MESSAGES.ADMIN_RESPONSIBLE,
      },
    }),
  };

  if (hasRehireOrRenewStage) {
    const lastStages =
      createAssignmentRenewOrRehireForMemberStageConfig(includeRehireStage);

    return { ...stages, ...lastStages };
  }

  return stages;
};

// It's necessary for BE to send such stage, but it's not needed for Profile Progress Stages on FE
const removeUnusedStagesForProfileProgress = (
  stages: AssignmentStageModel[],
) => {
  return stages.filter(
    ({ stage }) =>
      !isNil(stage) &&
      stage !== AssignmentStageEnum.Pending &&
      stage !== AssignmentStageEnum.ICContractorQuestionnaire &&
      stage !== AssignmentStageEnum.ICMemberQuestionnaire,
  );
};

interface AssignmentProgressStepsOptions {
  type: AssignmentTypeEnum;
  isAdmin?: boolean;
  includeRenewStage: boolean;
  includeRehireStage: boolean;
  hasRehireOrRenewStage: boolean;
}

const getAssignmentProgressSteps = (
  options: AssignmentProgressStepsOptions,
) => {
  const {
    isAdmin,
    type,
    includeRenewStage,
    includeRehireStage,
    hasRehireOrRenewStage,
  } = options;

  if (isAdmin) {
    switch (type) {
      case AssignmentTypeEnum.EEAssignment:
        return ADMIN_W2_ASSIGNMENT_PROGRESS_STEPS(
          includeRenewStage,
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      case AssignmentTypeEnum.ICAssignment:
        return ADMIN_IC_ASSIGNMENT_PROGRESS_STEPS(
          includeRenewStage,
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      case AssignmentTypeEnum.SCAssignment:
        return ADMIN_SC_ASSIGNMENT_PROGRESS_STEPS(
          includeRenewStage,
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      default:
        return [];
    }
  } else {
    switch (type) {
      case AssignmentTypeEnum.EEAssignment:
        return MEMBER_W2_ASSIGNMENT_PROGRESS_STEPS(
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      case AssignmentTypeEnum.ICAssignment:
        return MEMBER_IC_ASSIGNMENT_PROGRESS_STEPS(
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      case AssignmentTypeEnum.SCAssignment:
        return MEMBER_SC_ASSIGNMENT_PROGRESS_STEPS(
          includeRehireStage,
          hasRehireOrRenewStage,
        );

      default:
        return [];
    }
  }
};

interface ProfileStagesOptions {
  assignmentType: AssignmentTypeEnum;
  includeRenewStage: boolean;
  includeRehireStage: boolean;
}
const getAssignmentProfileStages = (options: ProfileStagesOptions) => {
  const { assignmentType, includeRenewStage, includeRehireStage } = options;

  switch (assignmentType) {
    case AssignmentTypeEnum.EEAssignment:
      return W2_ASSIGNMENT_PROFILE_STAGES(
        includeRenewStage,
        includeRehireStage,
      );

    case AssignmentTypeEnum.ICAssignment:
      return IC_ASSIGNMENT_PROFILE_STAGES(
        includeRenewStage,
        includeRehireStage,
      );

    case AssignmentTypeEnum.SCAssignment:
      return SC_ASSIGNMENT_PROFILE_STAGES(
        includeRenewStage,
        includeRehireStage,
      );

    default:
      return [];
  }
};

export interface AssignmentProgressStagesOptions {
  type: AssignmentTypeEnum;
  isAdmin?: boolean;
  renewStatus?: RenewStatusEnum;
  stages?: AssignmentStageModel[];
  renewKind?: RenewKindEnum;
  hasDuplicatedWorker?: boolean;
}

export const getAssignmentProgressStages = (
  stagesOptions: AssignmentProgressStagesOptions,
) => {
  const { type, isAdmin, stages = [], renewStatus, renewKind } = stagesOptions;

  const hasRehireOrRenewStage = Boolean(
    stages.find(
      (stage) =>
        stage.stage === AssignmentStageEnum.Rehired ||
        stage.stage === AssignmentStageEnum.Renewed,
    ),
  );

  const isRenewStatusValid = (status?: RenewStatusEnum) =>
    status === RenewStatusEnum.RenewRequest ||
    status === RenewStatusEnum.Renewed ||
    status === RenewStatusEnum.ParentRenewRequest;

  const includeRehireStage =
    renewKind === RenewKindEnum.Rehire && isRenewStatusValid(renewStatus);
  const includeRenewStage =
    renewKind === RenewKindEnum.Renew && isRenewStatusValid(renewStatus);

  const assignmentProfileStagesConfig = getAssignmentProgressSteps({
    type,
    isAdmin,
    includeRenewStage,
    includeRehireStage,
    hasRehireOrRenewStage,
  }) as AssignmentProgressStepConfig;

  const assignmentStages = stages?.length
    ? removeUnusedStagesForProfileProgress(stages)
    : [];

  const assignmentProfileStages = getAssignmentProfileStages({
    assignmentType: type,
    includeRenewStage,
    includeRehireStage,
  });

  const mergedProfileStages = map(
    [...assignmentStages, ...assignmentProfileStages],
    (stage) => {
      return typeof stage === 'number'
        ? {
            stage,
            stageStatus: StageStatusEnum.Pending,
          }
        : stage;
    },
  );

  const sortedProfileStages = orderBy(
    mergedProfileStages,
    [(step) => step.stage, (step) => step.stageStatus],
    ['asc', 'desc'],
  );

  const uniqProfileStages = uniqBy(
    sortedProfileStages,
    (stageConfig) => stageConfig.stage,
  );

  const isMemberQuestionnaireCompleteExists = stages.some(
    ({ stage }) => stage === AssignmentStageEnum.ICMemberQuestionnaire,
  );
  const isContractorQuestionnaireCompleteExists = stages.some(
    ({ stage }) => stage === AssignmentStageEnum.ICContractorQuestionnaire,
  );

  const profileStagesConfigs = uniqProfileStages
    .map(
      (
        {
          showPulsatingIndicator,
          additionalInfoRequest,
          additionalInfoReply,
          ...step
        },
        stepIndex,
        profileStages,
      ) => {
        const getStageConfig =
          assignmentProfileStagesConfig[step.stage as AssignmentStageEnum];

        if (!getStageConfig) return undefined;

        const arePrevStagesCompleted = arePreviousStagesCompleted(
          profileStages,
          step,
        );

        const stageConfig = getStageConfig({
          step,
          arePrevStagesCompleted,
          isMemberQuestionnaireCompleteExists,
          isContractorQuestionnaireCompleteExists,
        });

        const {
          messages,
          responsibleRole,
          isActionEnabled: isActionEnabledFromConfig,
          displayRequestButton: displayRequestButtonFromConfig,
          displayResponseButton: displayResponseButtonFromConfig,
          ...rest
        } = stageConfig;

        const enableRequestButton = shouldDisplayRequestButton({
          isAdmin,
          arePrevStagesCompleted,
          responsibleRole,
          stageStatus: step.stageStatus,
        });

        const enableResponseButton = shouldDisplayResponseButton({
          isAdmin,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
        });

        const stepValidationStatus = getProgressStepStatus({
          step,
          stepIndex,
          profileStages,
        });

        const isPulsatingStage =
          isSoftHardStopStage(step.stageStatus) &&
          !isAdmin &&
          showPulsatingIndicator;

        return {
          ...rest,
          ...step,
          responsibleRole: responsibleRole || UserRole.Admin,
          message: getStageMessage(step.stageStatus as StageStatusEnum, {
            ...messages,
            isAdmin,
            arePrevStagesCompleted,
          }),
          isPulsating: isPulsatingStage,
          requestMessage: additionalInfoRequest,
          responseMessage: additionalInfoReply,
          displayResponseButton: isNil(displayResponseButtonFromConfig)
            ? enableResponseButton
            : displayResponseButtonFromConfig,
          displayRequestButton: isNil(displayRequestButtonFromConfig)
            ? enableRequestButton
            : displayRequestButtonFromConfig,
          isActionEnabled: isNil(isActionEnabledFromConfig)
            ? isAdmin
            : isActionEnabledFromConfig,
          stepValidationStatus,
        };
      },
    )
    .filter(Boolean) as AssignmentProgressStep[];

  return profileStagesConfigs;
};

export const getAssignmentProgressTrackerStageName = (
  stage: AssignmentStageEnum,
) => {
  switch (stage) {
    case AssignmentStageEnum.EECandidateSentOnboarding:
      return 'Candidate sent onboarding';
    case AssignmentStageEnum.EERatesAndCustomerReviewed:
      return 'Rates and customer reviewed';
    case AssignmentStageEnum.EECandidateCompletedOnboarding:
      return 'Candidate completed onboarding';
    case AssignmentStageEnum.EEOnboardingPackageVerified:
      return 'Onboarding package verified';
    case AssignmentStageEnum.EEPreScreeningValidating:
      return 'Pre-screening validating';
    case AssignmentStageEnum.EEPreScreeningCompleted:
      return 'Pre-screening completed';
    case AssignmentStageEnum.EETimesheetAccountCreated:
      return 'Timesheet account created';
    case AssignmentStageEnum.EEAssignmentConfirmed:
      return 'Assignment confirmed';
    case AssignmentStageEnum.ICMemberQuestionnaire:
      return 'Member questionnaire';
    case AssignmentStageEnum.ICMemberQuestionnaireComplete:
      return 'Member questionnaire complete';
    case AssignmentStageEnum.ICRatesAndCustomerReviewed:
      return 'Rates and customer reviewed';
    case AssignmentStageEnum.ICContractorQuestionnaire:
      return 'Contractor questionnaire';
    case AssignmentStageEnum.ICContractorQuestionnaireComplete:
      return 'Classification questionnaire complete';
    case AssignmentStageEnum.ICContractorChecklistVerified:
      return 'Contractor checklist verified';
    case AssignmentStageEnum.ICContractorSentPackage:
      return 'Contractor sent package';
    case AssignmentStageEnum.ICAcquiringApplicableInsurances:
      return 'Acquiring applicable insurances';
    case AssignmentStageEnum.ICPreScreeningValidating:
      return 'Pre-screening validating';
    case AssignmentStageEnum.ICPreScreeningCompleted:
      return 'Pre-screening completed';
    case AssignmentStageEnum.ICVendorProfileFinalized:
      return 'Vendor profile finalized';
    case AssignmentStageEnum.ICFinalAgreementUploaded:
      return 'Final agreement uploaded';
    case AssignmentStageEnum.SubcontractorContractorSentPackage:
      return 'Contractor sent package';
    case AssignmentStageEnum.SubcontractorRatesAndCustomerReviewed:
      return 'Rates and customer reviewed';
    case AssignmentStageEnum.SubcontractorWorkAuthorizationVerified:
      return 'Work authorization verified';
    case AssignmentStageEnum.SubcontractorPreScreeningValidating:
      return 'Pre-screening validating';
    case AssignmentStageEnum.SubcontractorPreScreeningCompleted:
      return 'Pre-screening completed';
    case AssignmentStageEnum.SubcontractorVendorProfileFinalized:
      return 'Vendor profile finalized';
    case AssignmentStageEnum.SubcontractorFinalAgreementUploaded:
      return 'Final agreement uploaded';
    case AssignmentStageEnum.Renewed:
      return 'Assignment renewed';
    case AssignmentStageEnum.Rehired:
      return 'Assignment rehired';
  }
};
